"use client";

/**
 * Shared presentational pieces for the light login: the brand wordmark,
 * the trust line (shield icon), and the dark welcome panel used on desktop.
 */

import { BRAND_HOME_ARIA_LABEL, BRAND_LOGO_TITLE } from "@/lib/marketing-seo";
import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { ReactElement } from "react";
import { DotGridBackdrop } from "../../_components/marketing/dot-grid-backdrop";
import { DARK_PANEL_BG, INK } from "./login-shared";

type LogoMarkProps = {
  /** "light" → dark tile + paper glyph (on paper bg). "dark" → paper tile + dark glyph (on dark panel). */
  readonly variant: "light" | "dark";
  readonly size?: number;
};

function LogoMark({ variant, size = 30 }: LogoMarkProps): ReactElement {
  // "light" sits on the paper card → dark tile; "dark" sits on the dark panel
  // → light tile. Both are the shipped assets, not a redraw.
  return (
    <BrandLogoMark
      width={size}
      height={size}
      variant={variant === "light" ? "dark" : "light"}
    />
  );
}

type BrandLockupProps = {
  readonly variant: "light" | "dark";
  readonly size?: number;
};

/** Logo tile + "hausverwalter.ai" wordmark, linking to "/". */
export function BrandLockup({
  variant,
  size = 30,
}: BrandLockupProps): ReactElement {
  return (
    <Link
      href="/"
      aria-label={BRAND_HOME_ARIA_LABEL}
      title={BRAND_LOGO_TITLE}
      style={{ textDecoration: "none" }}
    >
      <BrandLockupMark variant={variant} size={size} />
    </Link>
  );
}

/** Same lockup without the home link — store app has no website to go back to. */
export function BrandLockupMark({
  variant,
  size = 30,
}: BrandLockupProps): ReactElement {
  const t = useTranslations("public.loginWelcomePanel");
  const onDark = variant === "dark";
  return (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: 11,
        color: onDark ? "#fff" : INK,
      }}
    >
      <LogoMark variant={variant} size={size} />
      <span
        style={{
          fontSize: onDark ? 17 : 18,
          fontWeight: 700,
          letterSpacing: "-0.01em",
        }}
      >
        {t.rich("hausverwalterAi", {
          suffix: (chunks) => (
            <span
              style={{ color: onDark ? "rgba(255,255,255,0.5)" : "#8A8D94" }}
            >
              {chunks}
            </span>
          ),
        })}
      </span>
    </span>
  );
}

/** "Verschlüsselte Verbindung · Server in Europa" with a shield icon. */
export function TrustLine(): ReactElement {
  const t = useTranslations("public.loginWelcomePanel");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        fontSize: 11.5,
        color: "rgba(11,15,25,0.42)",
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: "block" }}
        aria-hidden="true"
      >
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
      {t("verschluesselteVerbindungServerEuropa")}
    </div>
  );
}

/** Dark left panel for the desktop split card. */
export function LoginWelcomePanel(): ReactElement {
  const t = useTranslations("public.loginWelcomePanel");
  return (
    <div
      style={{
        position: "relative",
        flex: 1.1,
        background: DARK_PANEL_BG,
        color: "#fff",
        padding: "52px 48px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <DotGridBackdrop tone="dark" focus="top" />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <BrandLockup variant="dark" />

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "44px 0",
          }}
        >
          <h1
            style={{
              fontFamily:
                "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif",
              fontWeight: 600,
              fontSize: 47,
              lineHeight: 1.06,
              letterSpacing: "-0.035em",
              margin: 0,
              color: "#fff",
            }}
          >
            {t.rich("schoenWiederDaBist", {
              em: (chunks) => <em style={{ fontStyle: "normal" }}>{chunks}</em>,
            })}
          </h1>
          <p
            style={{
              fontSize: 16,
              lineHeight: 1.62,
              color: "rgba(255,255,255,0.64)",
              margin: "22px 0 0",
              maxWidth: 412,
            }}
          >
            {t("meldeMachGenauDa")}
          </p>
        </div>
      </div>
    </div>
  );
}
